import { useEffect, useRef } from 'react';
import api from '../../../lib/api';
import { useSessionStore } from '../../../store/sessionStore';

const POLL_INTERVAL_MS = 30_000;

/**
 * Keep the local group timers in sync with the server for an open
 * Exit/Emergency session. Offline-first: this only *corrects* local state,
 * it never blocks starting a timer.
 *
 * - Fetches live timers on mount, window focus, and visibilitychange.
 * - Polls every 30s while enabled and online (a 90-min countdown needs no
 *   sub-minute freshness; the 1s UI tick keeps the countdown smooth).
 * - Skips polling when offline; on reconnect it hydrates, re-POSTs unsynced
 *   'local' starts, and drains queued ends.
 *
 * @param {Object} params
 * @param {string|number|null} params.siteId
 * @param {boolean} params.enabled - true only for an open Exit/Emergency session
 */
export function useSessionTimerSync({ siteId, enabled }) {
  const hydrateTimersFromServer = useSessionStore((s) => s.hydrateTimersFromServer);
  const resyncLocalTimers = useSessionStore((s) => s.resyncLocalTimers);
  const drainPendingTimerEnds = useSessionStore((s) => s.drainPendingTimerEnds);

  // Keep the latest siteId in a ref so listeners don't need re-binding.
  const siteIdRef = useRef(siteId);
  siteIdRef.current = siteId;

  const fetchAndHydrate = useRef(async () => {});
  fetchAndHydrate.current = async () => {
    const sid = siteIdRef.current;
    if (sid == null) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    try {
      const { data } = await api.get('/session-timers', { params: { siteId: parseInt(sid, 10) } });
      hydrateTimersFromServer(sid, data?.timers || []);
    } catch {
      // Transient — the next poll / focus retries.
    }
  };

  useEffect(() => {
    if (!enabled || siteId == null) return undefined;

    // Initial fetch on mount / when the session becomes active.
    fetchAndHydrate.current();

    const onFocus = () => fetchAndHydrate.current();
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchAndHydrate.current();
      }
    };
    const onOnline = () => {
      // Reconnect: adopt server truth, push unsynced starts, drain queued ends.
      resyncLocalTimers();
      drainPendingTimerEnds();
      fetchAndHydrate.current();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);

    const intervalId = setInterval(() => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      fetchAndHydrate.current();
    }, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      clearInterval(intervalId);
    };
  }, [enabled, siteId, resyncLocalTimers, drainPendingTimerEnds]);
}
